import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { IconButton } from '@ui/buttons/IconButton.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import { getContainerHeight, getMapElement } from '@utils/mapElement.ts';

import { usePillDrag } from '../hooks/usePillDrag.ts';
import { type SheetPosition, stepPosition, useThreePositionDrag } from '../hooks/useThreePositionDrag.ts';

const MIN_DRAG_DISTANCE_PX = 20;
export const PILL_HANDLE_ZONE_HEIGHT = 25;
// Legacy consumers keep an exact 300ms snap; threePosition derives a px/ms speed from this so the
// collapsed<->default leg (a much shorter distance) doesn't look slower than default<->expanded.
const BASE_TRANSITION_MS = 300;
const MIN_TRANSITION_MS = 150;
const MAX_TRANSITION_MS = 450;

const heightForPosition = (
  heights: { collapsedComponentHeight: number; minComponentHeight: number; maxComponentHeight: number },
  position: SheetPosition,
): number => {
  if (position === 'collapsed') {
    return heights.collapsedComponentHeight;
  }
  if (position === 'expanded') {
    return heights.maxComponentHeight;
  }
  return heights.minComponentHeight;
};

// z-index is 152 so it sits above the map and map overlay
interface WrapperProps {
  $isOpen?: boolean;
  $isDragging?: boolean;
  $hasInnerScrollingElement?: boolean;
  $allowScrollWhenCollapsed?: boolean;
  $maxHeight?: string;
  $componentHeight?: string;
  /** Only set when `threePosition` is used; keeps the snap-to-anchor speed (px/ms) consistent across legs of very different lengths. */
  $transitionDurationMs?: number;
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  z-index: 152;
  overflow-y: ${props => {
    if (props.$isOpen && !props.$isDragging && props.$hasInnerScrollingElement) {
      return 'hidden';
    }
    if ((props.$isOpen && !props.$isDragging) || props.$allowScrollWhenCollapsed) {
      return 'auto';
    }
    return 'hidden';
  }};
  max-height: ${props => props.$maxHeight};
  height: ${props => (props.$isOpen && !props.$isDragging ? props.$maxHeight : props.$componentHeight)};
  border-top-left-radius: ${props => (props.$isOpen ? 0 : props.theme.standardRadius)};
  border-top-right-radius: ${props => (props.$isOpen ? 0 : props.theme.standardRadius)};
  box-shadow: 0px -4px 20px rgba(0, 0, 0, 0.1);
  background-color: #fff;

  &.static {
    transition: ${props => props.$transitionDurationMs ?? 300}ms ease-in-out height;
  }
`;

interface ContentWrapperProps {
  $hasAccordion?: boolean;
  $hasPill?: boolean;
  $isOpen?: boolean;
  $hasInnerScrollingElement?: boolean;
  $maxHeight?: string;
  $expandToMaxHeight?: boolean;
}

export const ContentWrapper = styled.div<ContentWrapperProps>`
  ${props => (props.$expandToMaxHeight ? 'box-sizing: border-box;' : null)};
  ${props => (!props.$hasAccordion ? 'padding-top: 19px;' : null)};
  padding-bottom: ${props => (props.$hasPill ? 0 : 1)}px;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  top: 0;
  max-height: ${props => (props.$hasInnerScrollingElement && props.$isOpen ? props.$maxHeight : 'none')};
  height: ${props => (props.$expandToMaxHeight && props.$isOpen ? props.$maxHeight : 'auto')};
`;

const Toggler = styled.div`
  width: 100%;
  height: 19px;
  display: flex;
  flex: 0 1 auto;
  justify-content: center;
  padding-top: 10px;
  ${Icon} {
    margin-top: -5px;
  }
`;

// hitArea sizes the 44x44px touch target independently of the visible pill.
const PILL = { width: 44, height: 5, top: 10, hitArea: 44 };

const PillToggler = styled.div`
  position: relative;
  flex: none;
  width: 100%;
  height: ${PILL_HANDLE_ZONE_HEIGHT}px;
`;

const PillButton = styled.button`
  position: absolute;
  z-index: 2;
  inset-block-start: 0;
  inset-inline-start: calc(50% - ${PILL.hitArea / 2}px);
  width: ${PILL.hitArea}px;
  height: ${PILL.hitArea}px;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.standardRadius};
  background: transparent;
  cursor: pointer;
  touch-action: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  user-select: none;
  -webkit-user-select: none;

  &::after {
    position: absolute;
    inset-block-start: ${PILL.top}px;
    inset-inline-start: 0;
    width: ${PILL.width}px;
    height: ${PILL.height}px;
    border-radius: 9999px;
    background-color: var(--color-secondary);
    content: '';
    opacity: 0.7;
  }
`;

interface ExpandableContentState {
  isOpen: boolean;
  isDraggable: boolean;
  isClicked: boolean;
  isDragging: boolean;
  isExpanding: boolean;
  /** Only meaningful when `threePosition` is set: true while dragging from 'default' toward 'collapsed'. */
  isCollapsing: boolean;
  /** Only meaningful when `threePosition` is set: tracks the third, lower sheet position. */
  position: SheetPosition;
  cursorPositionY: number;
  componentHeight: number;
  minComponentHeight: number;
  maxComponentHeight: number;
  /** Only meaningful when `threePosition` is set: auto-measured height of the collapsed composition. */
  collapsedComponentHeight: number;
  innerComponentHeight: number;
  innerComponentUpdated: boolean;
  /** Only meaningful when `threePosition` is set: speed-calibrated duration for the current snap transition. */
  transitionDurationMs: number;
}

interface ExpandableContentProps {
  ariaLabel?: string;
  id?: string;
  children: React.ReactElement<Record<string, unknown>>;
  className?: string;
  hasInnerScrollingElement?: boolean;
  maxHeight?: string;
  initialHeight?: string;
  isMapDisabled?: boolean;
  isDraggable?: boolean;
  bus: { send: (event: string, data?: Record<string, unknown>) => void };
  T: (key: string) => string;
  poi?: { name?: string };
  allowScrollWhenCollapsed?: boolean;
  /** The default pill captures handle-only drags; the optional chevron retains whole-sheet gestures. */
  handle?: 'chevron' | 'pill';
  /**
   * Fill maxHeight when expanded, independently of the handle or natural content height.
   * Keep this option fixed while the sheet is open.
   */
  expandToMaxHeight?: boolean;
  /**
   * Adds a third, lower position below the default one, reached by dragging the pill down or
   * pressing ArrowDown (never by click). Requires `handle="pill"`. The child receives `isCollapsed` /
   * `isCollapsing` in addition to the existing injected props.
   */
  threePosition?: boolean;
  /**
   * Id of an element describing the collapsed composition (e.g. its visible summary text).
   * Referenced by the pill's `aria-describedby` only while collapsed, so assistive tech gets the
   * same "what's showing" context sighted users get from the visible content.
   */
  collapsedDescriptionId?: string;
}

const ExpandableContent = ({
  ariaLabel,
  id,
  children,
  className,
  hasInnerScrollingElement = false,
  maxHeight = '100vh',
  initialHeight = '50vh',
  isMapDisabled = true,
  isDraggable: isDraggableDefault = false,
  bus,
  T,
  poi,
  allowScrollWhenCollapsed = false,
  handle = 'pill',
  expandToMaxHeight = false,
  threePosition = false,
  collapsedDescriptionId,
}: ExpandableContentProps) => {
  const [state, setState] = useState({
    isOpen: false,
    isDraggable: true,
    isClicked: false,
    isDragging: true,
    isExpanding: false,
    isCollapsing: false,
    position: 'default' as SheetPosition,
    cursorPositionY: 0,
    componentHeight: 0,
    minComponentHeight: 0,
    maxComponentHeight: 0,
    collapsedComponentHeight: 0,
    innerComponentHeight: 0,
    innerComponentUpdated: false,
    transitionDurationMs: BASE_TRANSITION_MS,
  });
  const myNode = useRef<HTMLDivElement>(null); // to contain outer-most node
  const elementRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef<ExpandableContentState | null>(null);

  const updateState = useCallback(
    (
      nextState: Partial<ExpandableContentState> | ((prev: ExpandableContentState) => Partial<ExpandableContentState>),
    ) => {
      setState(prevState => {
        const patch = typeof nextState === 'function' ? nextState(prevState) : nextState;
        const mergedState = { ...prevState, ...patch };
        // Outside threePosition, componentHeight must never go below the default/min anchor.
        // With threePosition, the collapsed anchor is allowed to be lower than that
        const floor = threePosition
          ? Math.min(mergedState.minComponentHeight, mergedState.collapsedComponentHeight)
          : mergedState.minComponentHeight;
        mergedState.componentHeight = Math.max(floor, mergedState.componentHeight);
        const hasChanged = Object.keys(mergedState).some(
          key => !Object.is((prevState as Record<string, unknown>)[key], (mergedState as Record<string, unknown>)[key]),
        );
        return hasChanged ? mergedState : prevState;
      });
    },
    [threePosition],
  );

  const calculateComponentHeight = useCallback((height: string | number): number => {
    const matched = height.toString().match(/\d+/g);
    if (!matched) {
      return typeof height === 'number' ? height : 0;
    }
    let componentHeight: number = parseInt(matched[0]); // get numbers from height passed in px or vh
    if (!componentHeight) {
      return 0;
    }

    if (/vh/.test(height.toString())) {
      // if the unit of height was vh (rather than px)
      componentHeight = getContainerHeight() * (componentHeight / 100); // calculate height in px from container height
    }

    return Math.floor(componentHeight);
  }, []);

  const checkIfIsDraggable = useCallback(
    (innerComponentHeight: number) => {
      if (isDraggableDefault) {
        return true;
      } // if the component is draggable by default

      if (!innerComponentHeight) {
        return false;
      }

      const heightMatched = initialHeight.match(/\d+/g);
      if (!heightMatched) {
        return false;
      }
      let componentHeight: number = parseInt(heightMatched[0]); // get numbers from height passed in px or vh
      if (!componentHeight) {
        return false;
      }

      if (/vh/.test(initialHeight)) {
        // if the unit of initialHeight was vh (rather than px)
        componentHeight = getContainerHeight() * (componentHeight / 100); // calculate height in px from container height
      }
      return innerComponentHeight > componentHeight; // if inner component is bigger than wrapper height, then make this component draggable
    },
    [initialHeight, isDraggableDefault],
  );

  // Initial layout setup and recalculation when configured heights change.
  useLayoutEffect(() => {
    if (!elementRef.current) {
      return;
    }
    const innerComponentHeight = elementRef.current.offsetHeight;

    updateState({
      innerComponentHeight,
      isDraggable: checkIfIsDraggable(innerComponentHeight),
      componentHeight: calculateComponentHeight(initialHeight),
      minComponentHeight: calculateComponentHeight(initialHeight),
      maxComponentHeight: calculateComponentHeight(maxHeight),
    });
  }, [calculateComponentHeight, checkIfIsDraggable, initialHeight, maxHeight, updateState]);

  useLayoutEffect(() => {
    if (handle !== 'pill' && !expandToMaxHeight) {
      return;
    }
    const remeasure = () => updateState({ innerComponentUpdated: true });
    const observer = new ResizeObserver(remeasure);
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    const mapElement = getMapElement();
    if (mapElement) {
      observer.observe(mapElement);
    }
    window.addEventListener('resize', remeasure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', remeasure);
    };
  }, [expandToMaxHeight, handle, updateState]);

  // Child content can change size without prop changes, so remeasure after commits.
  // This intentionally runs after every render; updateState returns the previous state
  // unless the measured or derived layout values actually changed.
  useLayoutEffect(() => {
    if (!elementRef.current) {
      return;
    }

    const innerComponentHeight = elementRef.current.offsetHeight;

    updateState(prevState => {
      const calculatedMaxHeight = calculateComponentHeight(maxHeight);
      const initialHeightCalc = calculateComponentHeight(initialHeight);

      if (!threePosition) {
        const newMinComponentHeight = expandToMaxHeight
          ? prevState.isOpen || prevState.isExpanding
            ? Math.min(prevState.minComponentHeight, initialHeightCalc, calculatedMaxHeight)
            : Math.min(innerComponentHeight, initialHeightCalc, calculatedMaxHeight)
          : Math.min(innerComponentHeight, initialHeightCalc);
        const newMaxComponentHeight =
          expandToMaxHeight || innerComponentHeight > calculatedMaxHeight ? calculatedMaxHeight : innerComponentHeight;
        const newComponentHeight =
          !prevState.isOpen && !prevState.isDragging ? newMinComponentHeight : prevState.componentHeight;

        if (
          prevState.innerComponentHeight === innerComponentHeight &&
          prevState.minComponentHeight === newMinComponentHeight &&
          prevState.maxComponentHeight === newMaxComponentHeight &&
          prevState.componentHeight === newComponentHeight
        ) {
          return {};
        }

        return {
          innerComponentHeight,
          isDraggable: checkIfIsDraggable(innerComponentHeight),
          maxComponentHeight: newMaxComponentHeight,
          minComponentHeight: newMinComponentHeight,
          componentHeight: newComponentHeight,
        };
      }

      // The mounted content reflects whichever position is currently "displayed": the committed
      // position, or the position being dragged toward once isExpanding/isCollapsing flips (content
      // swaps immediately once a drag crosses the threshold, ahead of the gesture actually ending).
      const displayed: SheetPosition = prevState.isExpanding
        ? 'expanded'
        : prevState.isCollapsing
          ? 'collapsed'
          : prevState.position;

      const newMinComponentHeight =
        displayed === 'default'
          ? expandToMaxHeight
            ? Math.min(innerComponentHeight, initialHeightCalc, calculatedMaxHeight)
            : Math.min(innerComponentHeight, initialHeightCalc)
          : prevState.minComponentHeight;

      const newCollapsedComponentHeight =
        displayed === 'collapsed' ? innerComponentHeight : prevState.collapsedComponentHeight;

      const newMaxComponentHeight =
        expandToMaxHeight || innerComponentHeight > calculatedMaxHeight ? calculatedMaxHeight : innerComponentHeight;

      const newComponentHeight = prevState.isDragging
        ? prevState.componentHeight
        : displayed === 'default'
          ? newMinComponentHeight
          : displayed === 'collapsed'
            ? newCollapsedComponentHeight
            : prevState.componentHeight;

      if (
        prevState.innerComponentHeight === innerComponentHeight &&
        prevState.minComponentHeight === newMinComponentHeight &&
        prevState.maxComponentHeight === newMaxComponentHeight &&
        prevState.collapsedComponentHeight === newCollapsedComponentHeight &&
        prevState.componentHeight === newComponentHeight
      ) {
        return {};
      }

      return {
        innerComponentHeight,
        isDraggable: checkIfIsDraggable(innerComponentHeight),
        maxComponentHeight: newMaxComponentHeight,
        minComponentHeight: newMinComponentHeight,
        collapsedComponentHeight: newCollapsedComponentHeight,
        componentHeight: newComponentHeight,
      };
    });
  });

  // React to measured height/open-state changes, similar to componentDidUpdate.
  useLayoutEffect(() => {
    const prevState = prevStateRef.current;
    prevStateRef.current = state;

    if (!prevState) {
      return;
    }

    const { innerComponentHeight, innerComponentUpdated, isOpen } = state;

    // Full-height mode blocks the map even for short content.
    // Otherwise, preserve the legacy content-height check.
    if (
      isMapDisabled &&
      prevState.isOpen !== isOpen &&
      (expandToMaxHeight || innerComponentHeight >= getContainerHeight())
    ) {
      bus.send('map/disableMap', { disable: isOpen });
    }

    if (
      prevState.innerComponentHeight !== innerComponentHeight ||
      ((handle === 'pill' || expandToMaxHeight) && prevState.minComponentHeight !== state.minComponentHeight) ||
      (threePosition && prevState.collapsedComponentHeight !== state.collapsedComponentHeight)
    ) {
      const calculatedMaxHeight = calculateComponentHeight(maxHeight);
      const initialHeightCalc = calculateComponentHeight(initialHeight);
      const newMinComponentHeight = expandToMaxHeight
        ? state.minComponentHeight
        : Math.min(innerComponentHeight, initialHeightCalc);
      const newMaxComponentHeight =
        expandToMaxHeight || innerComponentHeight > calculatedMaxHeight ? calculatedMaxHeight : innerComponentHeight;

      // Report the content's full height, plus how much of it is actually visible while collapsed
      // (the part that overlays the map), so listeners can keep map content clear of the panel.
      bus.send('expandableContent/reportHeight', {
        height: innerComponentHeight,
        visibleHeight: newMinComponentHeight,
      });

      updateState({
        isDraggable: checkIfIsDraggable(innerComponentHeight),
        maxComponentHeight: newMaxComponentHeight,
        componentHeight:
          state.isExpanding || state.isCollapsing
            ? state.componentHeight
            : isOpen
              ? initialHeightCalc
              : threePosition && state.position === 'collapsed'
                ? state.collapsedComponentHeight
                : newMinComponentHeight,
        minComponentHeight: newMinComponentHeight,
      });
    }

    if (innerComponentUpdated) {
      // wait until DOM is rendered to get element size
      window.requestAnimationFrame(() => {
        const node = myNode.current;
        if (node !== null) {
          updateState({ innerComponentHeight: elementRef.current?.offsetHeight ?? 0 });
        }
      });
      updateState({ innerComponentUpdated: false });
    }
  }, [
    bus,
    calculateComponentHeight,
    checkIfIsDraggable,
    expandToMaxHeight,
    handle,
    initialHeight,
    isMapDisabled,
    maxHeight,
    state,
    threePosition,
    updateState,
  ]);

  // Cleanup on unmount, matching the old componentWillUnmount behavior.
  useLayoutEffect(() => {
    return () => {
      bus.send('map/disableMap', { disable: false });
      bus.send('map/resize'); // Ensure map renders with 100% container size
    };
  }, [bus]);

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      updateState({ isOpen, isDragging: false, isExpanding: false, isCollapsing: false });
    },
    [updateState],
  );

  const setPosition = useCallback(
    (position: SheetPosition) => {
      updateState(prevState => {
        const fromHeight = prevState.isDragging
          ? prevState.componentHeight
          : heightForPosition(prevState, prevState.position);
        const toHeight = heightForPosition(prevState, position);
        const speed = (prevState.maxComponentHeight - prevState.minComponentHeight) / BASE_TRANSITION_MS;
        const transitionDurationMs =
          speed > 0
            ? Math.min(
                MAX_TRANSITION_MS,
                Math.max(MIN_TRANSITION_MS, Math.round(Math.abs(toHeight - fromHeight) / speed)),
              )
            : BASE_TRANSITION_MS;
        return {
          position,
          isOpen: position === 'expanded',
          isDragging: false,
          isExpanding: false,
          isCollapsing: false,
          transitionDurationMs,
        };
      });
    },
    [updateState],
  );

  // Check content height
  const refCallback = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        elementRef.current = element;
        updateState({ innerComponentHeight: element.offsetHeight });
      }
    },
    [updateState],
  );

  // calculate relative position to the mouse and set dragging=true
  const onMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();

    // only left mouse button
    if (e.button !== 0) {
      return;
    }

    updateState({ isClicked: true, cursorPositionY: e.pageY });
  };

  const onTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();

    updateState({ isClicked: true, cursorPositionY: e.touches[0].clientY });
  };

  const onTouchFinish = useCallback(
    (newCursorPositionY: number) => {
      const { cursorPositionY, isOpen, isDragging, maxComponentHeight, minComponentHeight } = state;

      const absY = Math.abs(newCursorPositionY - cursorPositionY);
      if (isDragging) {
        if (absY > MIN_DRAG_DISTANCE_PX) {
          if (newCursorPositionY < cursorPositionY) {
            updateState({ isOpen: true });
          }
          if (newCursorPositionY > cursorPositionY) {
            updateState({ isOpen: false });
          }
        } else {
          if (isOpen) {
            updateState({ componentHeight: maxComponentHeight });
          } else {
            updateState({ componentHeight: minComponentHeight }); // if the drag wasn't big enough, go back to previous state
          }
        }
      }

      updateState({ isClicked: false, isDragging: false });
    },
    [state, updateState],
  );

  useEffect(() => {
    if (!state.isClicked) {
      return;
    }

    const onWindowMouseUp = (event: MouseEvent) => {
      onTouchFinish(event.pageY);
    };

    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, [state.isClicked, onTouchFinish]);

  const onMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();

    const newCursorPositionY = e.pageY;
    onTouchFinish(newCursorPositionY);
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();

    const newCursorPositionY = e.changedTouches[0].clientY;
    onTouchFinish(newCursorPositionY);
  };

  const onMove = (newCursorPositionY: number) => {
    const { isOpen, cursorPositionY, maxComponentHeight, minComponentHeight } = state;

    const howManyPxDragged = (newCursorPositionY - cursorPositionY) * -1;

    const initialComponentHeight = isOpen ? maxComponentHeight : minComponentHeight;

    let newHeight = 0;
    if (initialComponentHeight + howManyPxDragged > maxComponentHeight) {
      newHeight = maxComponentHeight;
    } else {
      newHeight = initialComponentHeight + howManyPxDragged;
    }

    updateState({ componentHeight: Math.floor(newHeight) });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    const { isClicked, cursorPositionY, isDragging, isDraggable } = state;

    if (!isClicked || !isDraggable) {
      return;
    }
    if (!cursorPositionY || !isDragging) {
      updateState({ isDragging: true });
    }

    const newCursorPositionY = e.pageY;

    onMove(newCursorPositionY);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const { isClicked, isDragging, isDraggable } = state;

    if (!isClicked || !isDraggable) {
      return;
    }
    if (!isDragging) {
      updateState({ isDragging: true });
    }

    const newCursorPositionY = e.touches[0].clientY;

    onMove(newCursorPositionY);
  };

  const legacyPillHandlers = usePillDrag({
    isOpen: state.isOpen,
    onOpenChange: setIsOpen,
    openDirection: 'up',
    enabled: !threePosition,
    onDragMove: (distance, startedOpen) => {
      const initialComponentHeight = startedOpen ? state.maxComponentHeight : state.minComponentHeight;
      updateState({
        isDragging: true,
        isExpanding: !startedOpen && distance < 0,
        componentHeight: Math.floor(
          Math.max(state.minComponentHeight, Math.min(state.maxComponentHeight, initialComponentHeight - distance)),
        ),
      });
    },
  });

  const threePositionHandlers = useThreePositionDrag({
    position: state.position,
    onPositionChange: setPosition,
    enabled: threePosition,
    onDragMove: (distance, startedPosition) => {
      const targetPosition = stepPosition(startedPosition, distance < 0 ? 1 : -1);
      const fromHeight = heightForPosition(state, startedPosition);
      const toHeight = heightForPosition(state, targetPosition);
      const lowerBound = Math.min(fromHeight, toHeight);
      const upperBound = Math.max(fromHeight, toHeight);
      updateState({
        isDragging: true,
        // Only drags starting from 'default' swap content early; the collapsed <-> default and
        // default <-> expanded legs each keep showing their starting composition throughout.
        isExpanding: startedPosition === 'default' && distance < 0,
        isCollapsing: startedPosition === 'default' && distance > 0,
        componentHeight: Math.floor(Math.max(lowerBound, Math.min(upperBound, fromHeight - distance))),
      });
    },
  });

  const pillHandlers = threePosition ? threePositionHandlers : legacyPillHandlers;

  const notifyAboutChange = useCallback(() => {
    updateState({ innerComponentUpdated: true });
  }, [updateState]);

  const currentComponentHeight = () => {
    const { isOpen, componentHeight, isDragging, minComponentHeight, position, collapsedComponentHeight } = state;

    if (isDragging) {
      return componentHeight;
    }
    if (threePosition) {
      if (position === 'expanded') {
        return calculateComponentHeight(maxHeight);
      }
      if (position === 'collapsed') {
        return collapsedComponentHeight || minComponentHeight || calculateComponentHeight(initialHeight);
      }
      return minComponentHeight || calculateComponentHeight(initialHeight);
    }
    if (isOpen) {
      return calculateComponentHeight(maxHeight);
    }
    return minComponentHeight || calculateComponentHeight(initialHeight);
  };

  const { isOpen, isDraggable, isDragging, maxComponentHeight } = state;
  const canScroll = isOpen && !isDragging;
  // Stable id for the controlled content region
  const contentId = `${id || 'expandable'}-content`;
  // Full-height mode opts short content into modal behavior.
  // Other sheets keep their existing height-based modal detection.
  const isModal = isOpen && (expandToMaxHeight || maxComponentHeight === currentComponentHeight());

  return (
    <Wrapper
      data-expandable-content
      id={id}
      className={`${className} ${!isDragging && 'static'}`}
      $isOpen={isOpen}
      $isDragging={isDragging}
      $componentHeight={`${currentComponentHeight()}px`}
      $transitionDurationMs={threePosition ? state.transitionDurationMs : undefined}
      $maxHeight={!expandToMaxHeight && !isMapDisabled && isOpen ? '100vh' : `${maxComponentHeight}px`}
      $hasInnerScrollingElement={hasInnerScrollingElement}
      $allowScrollWhenCollapsed={allowScrollWhenCollapsed}
      onMouseDown={handle === 'chevron' ? onMouseDown : undefined}
      onTouchStart={handle === 'chevron' ? onTouchStart : undefined}
      onMouseUp={handle === 'chevron' ? onMouseUp : undefined}
      onTouchEnd={handle === 'chevron' ? onTouchEnd : undefined}
      onMouseMove={handle === 'chevron' ? onMouseMove : undefined}
      onTouchMove={handle === 'chevron' ? onTouchMove : undefined}
      ref={myNode}
      role={isModal ? 'dialog' : undefined}
      aria-modal={isModal || undefined}
      aria-label={ariaLabel ?? (poi ? poi.name : 'Expandable content')}
    >
      <ContentWrapper
        id={contentId}
        $hasAccordion={isDraggable}
        $hasPill={handle === 'pill'}
        ref={refCallback}
        $isOpen={isOpen || (expandToMaxHeight && state.isExpanding)}
        $hasInnerScrollingElement={hasInnerScrollingElement}
        $maxHeight={`${calculateComponentHeight(maxHeight)}px`}
        $expandToMaxHeight={expandToMaxHeight}
      >
        {isDraggable && handle === 'pill' && (
          <PillToggler>
            <PillButton
              data-testid="PillToggle"
              aria-label={T('ui:Toggle content')}
              aria-expanded={isOpen}
              aria-controls={contentId}
              aria-keyshortcuts={threePosition ? 'ArrowUp ArrowDown' : undefined}
              aria-describedby={threePosition && state.position === 'collapsed' ? collapsedDescriptionId : undefined}
              type="button"
              {...pillHandlers}
            />
          </PillToggler>
        )}
        {isDraggable && handle === 'chevron' && (
          <Toggler>
            <IconButton
              data-testid="ChevronToggle"
              aria-label={T('ui:Toggle content')}
              aria-expanded={isOpen}
              aria-controls={contentId}
              type="button"
              onClick={() => {
                updateState({ isOpen: !isOpen, isDragging: false });
              }}
            >
              <Icon
                className="icon nopointer"
                width={32}
                height={32}
                name={isOpen ? 'chevron-down' : 'chevron-up'}
                cdn
                fillColor="var(--color-secondary)"
                aria-hidden="true"
                focusable="false"
              />
            </IconButton>
          </Toggler>
        )}
        {React.cloneElement(children, {
          isOpen,
          isExpanding: state.isExpanding,
          isCollapsed: threePosition && state.position === 'collapsed',
          isCollapsing: threePosition && state.isCollapsing,
          setIsOpen,
          notifyAboutChange,
          canScroll,
        })}
      </ContentWrapper>
    </Wrapper>
  );
};

// The class version used React.PureComponent, which skipped renders when props
// were shallowly equal. memo keeps that behavior after converting to a function.
export default memo(ExpandableContent);

ExpandableContent.propTypes = {
  isMapDisabled: PropTypes.bool,
  bus: PropTypes.object,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isDraggable: PropTypes.bool,
  hasInnerScrollingElement: PropTypes.bool,
  allowScrollWhenCollapsed: PropTypes.bool,
  ariaLabel: PropTypes.string,
  handle: PropTypes.oneOf(['chevron', 'pill']),
  expandToMaxHeight: PropTypes.bool,
  threePosition: PropTypes.bool,
  collapsedDescriptionId: PropTypes.string,
};
